// eslint-disable-next-line object-curly-newline
import {
  Button,
  Icon,
  IconNames,
  SpinnerLoading,
  TextInput
} from 'og-merchant-portal-react-library';
import NotificationContext from 'og-merchant-portal-react-library/lib/NotificationContext/NotificationContext';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { FormattedHTMLMessage, FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import { Dropdown } from '@worldline/gaia';
import InformationTab from '../../common/components/InformationTab/InformationTab';
import LanguageSelector from '../../common/components/LanguageSelector/LanguageSelector';
import httpClient from '../../common/helpers/httpClient';
import ROUTES, { getRouteWithParam } from '../../common/helpers/routes';
import Captcha from './Captcha';
import { reducerError, reducerIcon, reducerInput } from './utils/reducers';
import {
  StyledButtonContainer,
  StyledCaptchaContainer,
  StyledCaptchaInputContainer,
  StyledContainer,
  StyledHeaderDiv,
  StyledInputContainer,
  StyledInputErrorText,
  StyledLanguageSelector,
  StyledLogoWrapper,
  StyledRefreshIconContainer,
  StyledSpinnerLoadingContainer,
  StyledTermsAndConditionsCheckbox,
  StyledTermsAndConditionsContainer,
  StyledTermsAndConditionsLabel
} from './utils/styles';
import {
  URLs,
  getTermsAndConditionsUrl,
  initalFormError,
  initalFormState,
  initalIcon,
  validateInputField,
  requiredFields
} from './utils/utils';
import COUNTRY_CODES from '../../common/constants';

const CreateForm = ({ intl }) => {
  const location = useLocation();
  const navigation = useNavigate();
  const [isp, setIsp] = useState('');
  const [stateInput, dispatchInput] = useReducer(reducerInput, initalFormState);
  const [stateError, dispatchError] = useReducer(reducerError, initalFormError);
  const [stateIcon, dispatchIcon] = useReducer(reducerIcon, initalIcon);
  const { addNotification } = useContext(NotificationContext);
  const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
  const [isButtonDisabled, setButtonDisabled] = useState(true);
  const [imageData, setImageData] = useState('');
  const [captchaKey, setCaptchaKey] = useState('');
  const [captchaText, setCaptchaText] = useState('');
  const [refreshCaptcha, setRefreshCaptcha] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [instanceSettings, setInstanceSettings] = useState({ termsAndConditions: [] });
  const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] = useState(false);
  const [termsAndConditionsUrl, setTermsAndConditionsUrl] = useState(undefined);
  const [ispSettings, setIspSettings] = useState({});
  const [settingsEndpointWasCalled, setSettingsEndpointWasCalled] = useState(false);
  const theme = useContext(ThemeContext);

  const getRequestedIsp = () => {
    const queryParams = new URLSearchParams(location.search);
    const requestedIsp = queryParams.get('isp')?.trim();

    return requestedIsp;
  };

  const getAllowedIspsFromSettings = settings => {
    return Array.isArray(settings?.allowedIsps) ? settings.allowedIsps : [];
  };

  const getTermsAndConditionsFromSettings = settings => {
    return Array.isArray(settings?.termsAndConditions) ? settings.termsAndConditions : [];
  };

  const resolveIspFromAllowedList = ({ requestedIsp, defaultIsp, allowedIsps }) => {
    const isRequestedIspAllowed = allowedIsps.some(item => {
      return item?.toLowerCase?.() === requestedIsp?.toLowerCase?.();
    });

    return {
      isRequestedIspAllowed,
      resolvedIsp: isRequestedIspAllowed ? requestedIsp : defaultIsp
    };
  };

  const fetchInstanceSettings = ({ instanceId, signal }) => {
    return httpClient.get(URLs.instanceSettingsUrl.replace('{instanceId}', instanceId), { signal });
  };

  const fetchIspSettings = ({ resolvedIsp, signal }) => {
    return httpClient
      .get(URLs.ispSettingsUrl.replace('{isp}', resolvedIsp), { signal })
      .catch(() => {
        return null;
      });
  };

  useEffect(() => {
    const controller = window.AbortController
      ? new window.AbortController()
      : {
        abort: () => { },
        signal: {
          aborted: false
        }
      };
    const { signal } = controller;

    const requestedIsp = getRequestedIsp();
    const instanceId = httpClient.getSetting('instanceId');
    const defaultIsp = httpClient.getSetting('isp');
    const isAborted = () => signal.aborted;

    const initialize = async () => {
      if (!requestedIsp) {
        setIsp(defaultIsp);
      }

      try {
        const settings = await fetchInstanceSettings({ instanceId, signal });

        if (isAborted()) {
          return;
        }

        const allowedIsps = getAllowedIspsFromSettings(settings);
        setInstanceSettings({
          termsAndConditions: getTermsAndConditionsFromSettings(settings)
        });

        if (!requestedIsp) {
          return;
        }

        const { isRequestedIspAllowed, resolvedIsp } = resolveIspFromAllowedList({
          requestedIsp,
          defaultIsp,
          allowedIsps
        });
        setIsp(resolvedIsp);

        if (!isRequestedIspAllowed) {
          navigation(ROUTES.home);
          return;
        }

        const ispSetting = await fetchIspSettings({ resolvedIsp, signal });

        if (isAborted()) {
          return;
        }

        setSettingsEndpointWasCalled(true);
        if (ispSetting) {
          setIspSettings(ispSetting);
        }
      } catch (error) {
        if (error?.name === 'AbortError' || isAborted()) {
          return;
        }

        setInstanceSettings({ termsAndConditions: [] });
        if (requestedIsp) {
          navigation(ROUTES.home);
        }
      }
    };

    initialize();

    return () => {
      controller.abort();
    };
    // As this check is performed based on URL parameters, it should be a one-time call
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCaptchaLoading(true);
    httpClient
      .get(URLs.captchaUrl)
      .then(result => {
        setImageData(result.imageData);
        setCaptchaKey(result.key);
        setCaptchaText(result.captchaText);
        setCaptchaLoading(false);
      })
      .catch(() => {
        setCaptchaLoading(false);
      });
  }, [refreshCaptcha]);

  const onInputChange = e => {
    const { name, value } = e.target;
    dispatchInput({ key: name, value });
    dispatchError({ type: 'reset_error', key: name });
    const errors = validateInputField(name, value);
    const errorKeys = Object.keys(errors);
    if (errorKeys.length) {
      const updatedError = {};
      errorKeys.forEach(key => {
        updatedError[key] = (
          <StyledInputErrorText>
            <FormattedMessage id={errors[key].id} defaultMessage={errors[key].defaultMessage} />
          </StyledInputErrorText>
        );
      });
      dispatchError({ type: 'update_error', key: name, value: updatedError[name] });
      dispatchIcon({ type: 'error_icon', key: name });
    } else if (!requiredFields.includes(name) && !value.trim()) {
      dispatchIcon({ type: 'reset_icon', key: name });
    } else {
      dispatchIcon({ type: 'success_icon', key: name });
    }
  };

  const onCountryInputChange = value => {
    dispatchInput({ key: 'country', value });
    setTermsAndConditionsAccepted(false);
  };

  const onTermsAndConditionsChange = e => {
    setTermsAndConditionsAccepted(e.target.checked);
  };

  const submitHandler = e => {
    e.preventDefault();
    setIsSpinnerLoading(true);

    const { merchantId, firstName, lastName, companyName, emailAddress, country, captchaInput } = stateInput;
    const captchaRequest = {
      captchaInput,
      captchaText,
      key: captchaKey
    };
    const accountCreationRequest = {
      accountInfo: {
        isp,
        firstName,
        lastName,
        pspid: merchantId?.trim() ? merchantId : null,
        companyName,
        emailAddress,
        countryCode: country
      },
      captcha: {
        captchaInput,
        captchaText,
        key: captchaKey
      }
    };
    httpClient
      .post(URLs.captchaUrl, captchaRequest)
      .then(() => {
        httpClient
          .post(URLs.accountCreationUrl, accountCreationRequest)
          .then(response => {
            setIsSpinnerLoading(false);

            const { pspid } = response;
            const { ssoUserCreated } = response;

            const requestedIsp = getRequestedIsp();
            const route = requestedIsp ? getRouteWithParam(ROUTES.success, 'isp', requestedIsp) : ROUTES.success;

            navigation(route, {
              state: {
                emailAddress,
                pspid,
                ssoUserCreated
              }
            });
          })
          .catch(error => {
            setIsSpinnerLoading(false);

            if (error?.status === 409) {
              dispatchError({
                type: 'update_error',
                key: 'merchantId',
                value: (
                  <StyledInputErrorText>
                    <FormattedMessage
                      id="developer.account.creation.form.validation.merchantIdExists"
                      defaultMessage="Merchant ID already exists"
                    />
                  </StyledInputErrorText>
                )
              });
              dispatchIcon({ type: 'error_icon', key: 'merchantId' });
              dispatchInput({ key: 'captchaInput', value: '' });
              setRefreshCaptcha(!refreshCaptcha);
              return;
            }

            addNotification({
              text: (
                <InformationTab
                  id="developer.account.creation.error"
                  defaultMessage="Something went wrong. Your account has not been created. Please try again later"
                  type="error"
                />
              ),
              type: 'error'
            });
            setRefreshCaptcha(!refreshCaptcha);
          });
      })
      .catch(() => {
        setIsSpinnerLoading(false);
        addNotification({
          text: (
            <InformationTab
              id="developer.capthca.validation.error"
              defaultMessage="Error validating captcha. Please try again"
              type="error"
            />
          ),
          type: 'error'
        });
        dispatchInput({ key: 'captchaInput', value: '' });
        setRefreshCaptcha(!refreshCaptcha);
      });
  };

  useEffect(() => {
    const nextUrl = getTermsAndConditionsUrl(
      instanceSettings.termsAndConditions,
      stateInput.country,
      intl.locale
    );
    setTermsAndConditionsUrl(nextUrl);
  }, [instanceSettings.termsAndConditions, stateInput.country, intl.locale]);

  useEffect(() => {
    const hasAnyError = Object.values(stateError).some(value => Boolean(value));
    const hasAllValue = requiredFields.every(field => Boolean(stateInput[field]));
    const hasTermsAndConditionsLink = Boolean(termsAndConditionsUrl);
    const hasRequiredTermsAcceptance = !hasTermsAndConditionsLink || termsAndConditionsAccepted;

    setButtonDisabled(hasAnyError || !hasAllValue || !hasRequiredTermsAcceptance);
  }, [stateError, stateInput, termsAndConditionsAccepted, termsAndConditionsUrl]);

  const getCountries = () => {
    return COUNTRY_CODES.map(countryCode => ({
      label: intl.formatMessage({
        id: `country.${countryCode}`,
        defaultMessage: countryCode.toUpperCase()
      }),
      value: countryCode
    })).sort((a, b) => a.label.localeCompare(b.label));
  };

  const getLogoSrc = () => {
    const defaultLogoSrc = 'themes/logo-expanded.svg';
    const ispSettingsLogo = ispSettings?.logo;

    if (ispSettingsLogo) {
      return ispSettingsLogo;
    }

    if (Boolean(getRequestedIsp()) && !settingsEndpointWasCalled) {
      return null;
    }

    return defaultLogoSrc;
  };

  const logoSrc = getLogoSrc();

  return (
    <>
      {isSpinnerLoading && (
        <StyledSpinnerLoadingContainer data-test="styled_spinner_loader_container">
          <SpinnerLoading />
        </StyledSpinnerLoadingContainer>
      )}
      <StyledContainer data-test="styled_container" style={isSpinnerLoading ? { display: 'none' } : undefined}>
        <StyledLogoWrapper data-test="logo-wrapper">
          {logoSrc && (
            <img
              src={logoSrc}
              alt="logo"
              data-test={`logo-${isp}`}
            />
          )}
        </StyledLogoWrapper>
        <StyledHeaderDiv data-test="styled_header">
          <FormattedMessage
            id="developer.account.creation.subheader"
            defaultMessage="Create your test account"
          />
        </StyledHeaderDiv>
        <StyledInputContainer data-test="styled_input_container_firstName">
          <TextInput
            data-test="text_input_firstName"
            label={intl.formatMessage({
              id: 'developer.account.creation.form.firstName.label',
              defaultMessage: 'First name'
            })}
            name="firstName"
            value={stateInput.firstName}
            onChange={onInputChange}
            onKeyDown={onInputChange}
            errorMessage={stateError.firstName}
            icon={stateIcon.firstName}
            iconAlign="right"
          />
        </StyledInputContainer>
        <StyledInputContainer data-test="styled_input_container_lastName">
          <TextInput
            data-test="text_input_lastName"
            label={intl.formatMessage({
              id: 'developer.account.creation.form.lastName.label',
              defaultMessage: 'Last name'
            })}
            name="lastName"
            value={stateInput.lastName}
            onChange={onInputChange}
            onKeyDown={onInputChange}
            errorMessage={stateError.lastName}
            icon={stateIcon.lastName}
            iconAlign="right"
          />
        </StyledInputContainer>

        <StyledInputContainer data-test="styled_input_container_merchantId">
          <TextInput
            data-test="text_input_merchantId"
            label={intl.formatMessage({
              id: 'developer.account.creation.form.merchantId.label',
              defaultMessage: 'Merchant ID'
            })}
            name="merchantId"
            value={stateInput.merchantId}
            onChange={onInputChange}
            onKeyDown={onInputChange}
            errorMessage={stateError.merchantId}
            icon={stateIcon.merchantId}
            iconAlign="right"
          />
        </StyledInputContainer>
        <StyledInputContainer data-test="styled_input_container_companyName">
          <TextInput
            data-test="text_input_companyName"
            label={intl.formatMessage({
              id: 'developer.account.creation.form.company.label',
              defaultMessage: 'Company name'
            })}
            name="companyName"
            value={stateInput.companyName}
            onChange={onInputChange}
            onKeyDown={onInputChange}
            errorMessage={stateError.companyName}
            icon={stateIcon.companyName}
            iconAlign="right"
          />
        </StyledInputContainer>
        <StyledInputContainer data-test="styled_input_container_emailAddress">
          <TextInput
            data-test="text_input_emailAddress"
            label={intl.formatMessage({
              id: 'developer.account.creation.form.email.label',
              defaultMessage: 'Email address'
            })}
            name="emailAddress"
            value={stateInput.emailAddress}
            onChange={onInputChange}
            onKeyDown={onInputChange}
            errorMessage={stateError.emailAddress}
            icon={stateIcon.emailAddress}
            iconAlign="right"
          />
        </StyledInputContainer>
        <StyledInputContainer data-test="styled_input_container_country">
          <Dropdown
            dataTest="country_selector"
            onChange={onCountryInputChange}
            options={getCountries()}
            value={stateInput.country}
            variant="DropdownWithBorder"
            panelPosition='left'
            label={intl.formatMessage({
              id: 'developer.account.creation.form.countryDropdown.label',
              defaultMessage: 'Country'
            })}
          />
        </StyledInputContainer>
        <StyledCaptchaContainer data-test="styled_captcha_container">
          <Captcha imageData={imageData} loading={captchaLoading} />
          <StyledRefreshIconContainer
            data-test="styled_refresh_icon_container"
            onClick={() => setRefreshCaptcha(!refreshCaptcha)}
          >
            <Icon
              data-test="developer.refresh"
              name={IconNames.refresh}
              color={theme.colors.primary.regular}
              size="1.7rem"
            />
          </StyledRefreshIconContainer>
        </StyledCaptchaContainer>
        <StyledCaptchaInputContainer data-test="styled_input_container_captcha">
          <TextInput
            data-test="text_input_captcha"
            placeholder={intl.formatMessage({
              id: 'developer.capthca.placeholder',
              defaultMessage: 'Please type the text above'
            })}
            name="captchaInput"
            value={stateInput.captchaInput}
            onChange={onInputChange}
            onKeyDown={onInputChange}
            errorMessage={stateError.captchaInput}
            icon={stateIcon.captchaInput}
            iconAlign="right"
          />
        </StyledCaptchaInputContainer>
        {Boolean(termsAndConditionsUrl) && (
          <StyledTermsAndConditionsContainer data-test="styled_terms_and_conditions_container">
            <StyledTermsAndConditionsCheckbox
              id="terms_and_conditions"
              data-test="terms_and_conditions_checkbox"
              type="checkbox"
              checked={termsAndConditionsAccepted}
              onChange={onTermsAndConditionsChange}
            />
            <StyledTermsAndConditionsLabel htmlFor="terms_and_conditions" data-test="terms_and_conditions_label">
              <FormattedHTMLMessage
                id="developer.account.creation.form.termsAndConditions.text"
                defaultMessage="I accept the <a href='{termsAndConditionsUrl}' target='_blank' rel='noopener noreferrer' data-test='terms_and_conditions_link'>Terms and Conditions</a>"
                values={{ termsAndConditionsUrl }}
              />
            </StyledTermsAndConditionsLabel>
          </StyledTermsAndConditionsContainer>
        )}
        <StyledButtonContainer data-test="styled_button_container">
          <Button data-test="submit_button" disabled={isButtonDisabled} onClick={submitHandler}>
            <FormattedMessage
              id="developer.account.creation.submit.button"
              defaultMessage="Create an account"
            />
          </Button>
        </StyledButtonContainer>
        <StyledLanguageSelector data-test="styled_language_selector">
          <LanguageSelector />
        </StyledLanguageSelector>
      </StyledContainer>
    </>
  );
};
CreateForm.propTypes = {
  intl: intlShape.isRequired
};

export default injectIntl(CreateForm);
